/**
 * Plans Admin (Platform Owner only) — CRUD for pricing packages.
 * Supports monthly / quarterly / half_yearly / annual / trial cycles with custom INR+USD pricing.
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const EMPTY = {
  name: "", code: "", billing_cycle: "monthly",
  price_inr: 0, price_usd: 0, seat_limit: 10, trial_days: 0,
  features: [], active: true, display_order: 100, description: "", highlight: false,
};

const FEATURE_GROUPS = [
  { label: "Core", keys: ["dashboard", "people", "org_chart", "attendance", "leaves", "approvals"] },
  { label: "Work", keys: ["tasks", "projects", "inbox", "messaging", "calendar", "meetings"] },
  { label: "Content", keys: ["documents", "announcements", "holidays"] },
  { label: "Lifecycle", keys: ["onboarding", "offboarding", "assets"] },
  { label: "Services", keys: ["claims", "helpdesk", "performance", "recruitment"] },
  { label: "HR Ops", keys: ["payroll", "bulk_import", "shifts", "multi_level_approval"] },
  { label: "Advanced", keys: ["ai", "audit_log", "feature_controls", "custom_branding", "sso"] },
];

export default function PlansAdmin() {
  const { user } = useAuth();

  const [plans, setPlans] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => { if (user?.is_platform_owner) load(); }, [user]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data.plans || []);
    } catch (e) { toast.error("Failed to load plans"); }
  };

  if (!user?.is_platform_owner) {
    return <div className="p-6 text-rose-600">Platform-owner access only.</div>;
  }

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, code: p.code, billing_cycle: p.billing_cycle,
      price_inr: p.price_inr || 0, price_usd: p.price_usd || 0,
      seat_limit: p.seat_limit || 10, trial_days: p.trial_days || 0,
      features: p.features || [], active: p.active ?? true,
      display_order: p.display_order || 100, description: p.description || "",
      highlight: p.highlight || false,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const { code, ...patch } = form;
        await api.patch(`/admin/plans/${editing.id}`, patch);
        toast.success("Plan updated");
      } else {
        if (!form.code || !form.name) return toast.error("Name and code required");
        await api.post("/admin/plans", form);
        toast.success("Plan created");
      }
      setOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const softDelete = async (p) => {
    if (!window.confirm(`Deactivate plan "${p.name}"? Existing subscribers keep access; new checkouts blocked.`)) return;
    try {
      await api.delete(`/admin/plans/${p.id}`);
      toast.success("Deactivated");
      await load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const toggleFeature = (key) => {
    setForm((f) => ({ ...f, features: f.features.includes(key) ? f.features.filter((k) => k !== key) : [...f.features, key] }));
  };

  const fmt = (v, cur) => cur === "inr" ? `₹${(v / 100).toLocaleString()}` : `$${(v / 100).toLocaleString()}`;

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Platform</div>
          <h1 className="text-2xl font-bold font-[Outfit]">Plans Management</h1>
          <p className="text-sm text-slate-500 mt-1">Define the pricing packages your tenants can subscribe to. Changes apply immediately across the SaaS.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive</label>
          <Button onClick={startCreate} className="bg-cyan-600 hover:bg-cyan-700" data-testid="new-plan-btn">
            <Plus className="w-4 h-4 mr-1" /> New Plan
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Cycle</th>
                <th className="text-right p-3">₹ INR</th>
                <th className="text-right p-3">$ USD</th>
                <th className="text-right p-3">Seats</th>
                <th className="text-center p-3">Features</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.filter((p) => showInactive || p.active).map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`plan-row-${p.code}`}>
                  <td className="p-3 font-mono text-xs">{p.code}</td>
                  <td className="p-3 font-medium">{p.name} {p.highlight && <Star className="inline w-3 h-3 text-amber-500" />}</td>
                  <td className="p-3 capitalize">{p.billing_cycle.replace("_", " ")}</td>
                  <td className="p-3 text-right font-mono">{fmt(p.price_inr || 0, "inr")}</td>
                  <td className="p-3 text-right font-mono">{fmt(p.price_usd || 0, "usd")}</td>
                  <td className="p-3 text-right">{p.seat_limit}</td>
                  <td className="p-3 text-center"><Badge variant="outline">{p.features?.length || 0}</Badge></td>
                  <td className="p-3 text-center">
                    <Badge variant={p.active ? "default" : "outline"} className={p.active ? "bg-emerald-600" : ""}>
                      {p.active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`edit-plan-${p.code}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {p.active && (
                      <Button size="sm" variant="outline" className="ml-1 text-rose-600 hover:bg-rose-50" onClick={() => softDelete(p)} data-testid={`del-plan-${p.code}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {plans.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-400">No plans yet. Click "New Plan" to create one.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit "${editing.name}"` : "Create new plan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plan name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="plan-name" />
              </div>
              <div>
                <Label>Code (unique slug) *</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!editing} className="mt-1 font-mono" placeholder="e.g. starter_monthly" data-testid="plan-code" />
              </div>
              <div>
                <Label>Billing cycle</Label>
                <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger className="mt-1" data-testid="plan-cycle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="half_yearly">Half-Yearly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Seat limit</Label>
                <Input type="number" value={form.seat_limit} onChange={(e) => setForm({ ...form, seat_limit: +e.target.value })} className="mt-1" data-testid="plan-seats" />
              </div>
              <div>
                <Label>Price (₹ INR) — in paise</Label>
                <Input type="number" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: +e.target.value })} className="mt-1 font-mono" data-testid="plan-inr" />
                <p className="text-[10px] text-slate-400 mt-0.5">Display: {fmt(form.price_inr, "inr")}</p>
              </div>
              <div>
                <Label>Price ($ USD) — in cents</Label>
                <Input type="number" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: +e.target.value })} className="mt-1 font-mono" data-testid="plan-usd" />
                <p className="text-[10px] text-slate-400 mt-0.5">Display: {fmt(form.price_usd, "usd")}</p>
              </div>
              <div>
                <Label>Trial days</Label>
                <Input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: +e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Display order</Label>
                <Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: +e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" placeholder="Tagline shown on the pricing card" />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="plan-active" /><Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.highlight} onCheckedChange={(v) => setForm({ ...form, highlight: v })} /><Label>Highlight (Most popular)</Label>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Included features ({form.features.length} selected)</Label>
              <div className="space-y-3">
                {FEATURE_GROUPS.map((g) => (
                  <div key={g.label}>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">{g.label}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.keys.map((key) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleFeature(key)}
                          className={`text-xs px-2 py-1 rounded border transition ${form.features.includes(key) ? "bg-cyan-50 border-cyan-500 text-cyan-900" : "border-slate-200 hover:bg-slate-50"}`}
                        >
                          {key}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-plan-btn">{editing ? "Save changes" : "Create plan"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
